import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { EMERGENCY_PHONE_NOTICE, normalizeEmergencyPhone } from '@mdt/contracts';
import { Button, Checkbox, ErrorAlert, TextField } from '../components/kit.tsx';
import { command } from '../lib/api.ts';
import { usePageTitle } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';

/**
 * The step a student still owes (SessionView.required, ADR-14): e.g. a roster student an admin linked to
 * their account. Write-only: the number is never shown back. No draft autosave (personal data).
 */
export function EmergencyPhone() {
  const { t, i18n } = useTranslation();
  const { refresh } = useSession();
  usePageTitle(t('emergencyPhone.title'));
  const [phone, setPhone] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    if (normalizeEmergencyPhone(phone) === null) return setError(t('onboarding.errors.emergencyPhone'));
    if (!accepted) return setError(t('onboarding.errors.notice'));
    setError(null);
    setBusy(true);
    try {
      await command('identity.setOwnEmergencyPhone', { emergencyPhone: phone, noticeVersion: EMERGENCY_PHONE_NOTICE.version, noticeAccepted: true });
      setPhone('');
      await refresh();
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" noValidate onSubmit={(e) => void submit(e)}>
      <h1>{t('emergencyPhone.title')}</h1>
      <p>{t('emergencyPhone.intro')}</p>
      <ErrorAlert error={failure} />
      <TextField label={t('onboarding.emergencyPhone')} hint={t('onboarding.emergencyPhoneHint')} required type="tel" inputMode="tel"
        autoComplete="tel" value={phone} error={error} onChange={(e) => setPhone(e.currentTarget.value)} />
      <div className="notice" id="pdpa-notice-2">{i18n.language === 'th' ? EMERGENCY_PHONE_NOTICE.th : EMERGENCY_PHONE_NOTICE.en}</div>
      <Checkbox label={t('onboarding.acceptNotice')} checked={accepted} describedBy="pdpa-notice-2" onChange={setAccepted} />
      <Button type="submit" busy={busy}>{t('emergencyPhone.save')}</Button>
    </form>
  );
}
